import { useEffect } from 'react';

import { FormProvider, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ArrowLeft } from 'lucide-react';
import styled from 'styled-components';

import { PAGES } from 'constants/pages';

import {
    RitualRepetition,
    useGetDetailedRitualQuery,
    useGetRitualGameForRitualQuery,
    useUpdateRitualMutation,
    TotadminPermission,
} from 'data/__generated__';

import { Header, HeaderTitle } from 'components/Header';
import { Loader } from 'components/Loader';
import { ActionsAndSave, type PageActionGroup } from 'components/PageHeader';
import { AuditLogsSection } from 'components/AuditLogs/AuditLogsSection';
import { PaymentMethodPaymentsSection } from 'pages/Payments/components/PaymentMethodPaymentsSection';
import { PageTitle } from 'components/PageTitle';

import { StatesSection } from 'pages/Rituals/components/StatesSection';
import { RitualFormContent } from './components/RitualFormContent';
import { TypeSection } from './components/TypeSection';

type FormData = {
    activeDate: string;
    activeWeekdays: boolean[];
    associatedDiscount: number;
    billToOrganizationId: string | null;
    expiringProductsOnly: boolean;
    image: File;
    messagesSetId: string | null;
    name: string;
    repetition: RitualRepetition;
    requiresParticipation: boolean;
    resetsBetweenPurchases: boolean;
    restrictedBadgeIds: string[];
    restrictedCategoryIds: string[];
    restrictedOrganizationIds: string[];
    restrictedProductIds: string[];
    restrictedSiteIds: string[];
    restrictedSubcategoryIds: string[];
    restrictedTagIds: string[];
    restrictedUserIds: string;
    timeWindow: {
        start: string;
        end: string;
    };
    supplierPartnershipId: string | null;
};

type ParamTypes = {
    ritualId: string;
};

export const RitualDetails = () => {
    const { ritualId = '' } = useParams<ParamTypes>();

    const {
        loading: ritualLoading,
        data: ritualData,
        error: ritualError,
    } = useGetDetailedRitualQuery({
        variables: { ritualId },
    });

    const {
        loading: ritualGameLoading,
        data: ritualGameData,
        error: ritualGameError,
        refetch,
    } = useGetRitualGameForRitualQuery({ variables: { ritualId } });

    const [updateRitual, { loading: updateRitualLoading }] = useUpdateRitualMutation();

    useEffect(() => {
        if (ritualGameData?.getRitualGameForRitual?.type) {
            refetch();
        }
    }, [ritualGameData?.getRitualGameForRitual?.type, refetch]);

    const methods = useForm<FormData>();
    const {
        handleSubmit,
        formState: { isDirty },
        reset,
    } = methods;

    if (ritualLoading || ritualGameLoading) {
        return (
            <Container>
                <Loader />
            </Container>
        );
    }

    if (!ritualData || ritualError || !ritualGameData || ritualGameError) {
        throw new Error('Une erreur est survenue lors de la récupération du rituel');
    }

    const onSubmit = handleSubmit(async (fields) => {
        const { activeDate, activeWeekdays, repetition } = fields;
        const updateInput = {
            ...fields,
            activeDate: repetition === RitualRepetition.Punctual ? activeDate : null,
            activeWeekdays: repetition === RitualRepetition.Weekly ? activeWeekdays : null,
            restrictedUserIds: fields.restrictedUserIds ? fields.restrictedUserIds.trim().split(',') : [],
        };
        const { data } = await updateRitual({
            variables: {
                ritualId,
                updateInput,
            },
        });

        if (data) {
            const {
                updateRitual: { updatedRitual, success, errors },
            } = data;
            if (success && updatedRitual) {
                toast.success('Le rituel a bien été mis à jour !');
                reset(fields);
            } else {
                if (errors) {
                    errors.forEach((error) => {
                        toast.error(error, { autoClose: false });
                    });

                    if (errors.length > 1) {
                        toast.info('Cliquez pour fermer toutes les notifications', {
                            autoClose: false,
                            onClick: () => toast.dismiss(),
                        });
                    }
                } else {
                    throw Error("Une erreur inconnue s'est produite");
                }
            }
        } else {
            throw Error("Une erreur inconnue s'est produite");
        }
    });

    const { detailedRitual } = ritualData;
    const { getRitualGameForRitual: ritualGame } = ritualGameData;

    if (!detailedRitual) {
        throw new Error('Une erreur est survenue lors de la récupération du rituel');
    }

    const actionGroups: PageActionGroup[] = [
        {
            label: 'Navigation',
            items: [{ label: 'Retour à la liste', icon: <ArrowLeft />, to: '/rituals' }],
        },
    ];

    return (
        <Container>
            <FormProvider {...methods}>
                <Form onSubmit={onSubmit}>
                    <Header>
                        <HeaderTitle>
                            <PageTitle page={PAGES.ritualDetails} />
                        </HeaderTitle>
                        <ActionsAndSave
                            actionGroups={actionGroups}
                            onSave={onSubmit}
                            saveDisabled={!isDirty}
                            saveLoading={updateRitualLoading}
                            saveTitle="Mettre à jour"
                            // Le formulaire n'a pas de valeurs par défaut : sans le droit, le sélecteur de
                            // sous-catégories n'est pas monté et `restrictedSubcategoryIds` (requis) manquerait.
                            saveRequiredPermissions={[
                                TotadminPermission.RitualsManage,
                                TotadminPermission.CategoriesRead,
                            ]}
                        />
                    </Header>
                    <Content>
                        <ScrollableContent>
                            <RitualFormContent ritual={detailedRitual} />
                            <StatesSection ritual={detailedRitual} isDirty={isDirty} />
                            <TypeSection ritual={detailedRitual} ritualGame={ritualGame} isDirty={isDirty} />
                            <PaymentMethodPaymentsSection
                                paymentMethodId={ritualId}
                                emptyMessage="Aucun paiement avec ce rituel"
                                dataTest="ritual-payments"
                            />
                            <AuditLogsSection collectionDocumentIds={[ritualId]} />
                        </ScrollableContent>
                    </Content>
                </Form>
            </FormProvider>
        </Container>
    );
};

const Container = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    background-color: ${({ theme }) => theme.backgroundColor};
`;

const Form = styled.form`
    display: flex;
    flex-direction: column;
    flex: 1;
    height: 100%;
`;

const Content = styled.div`
    flex: 1;
    overflow: hidden;
    background-color: ${({ theme }) => theme.backgroundColor};
`;

const ScrollableContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 15px;
    width: 100%;
    height: 100%;
    overflow-y: auto;
`;
